import { useEffect, useState } from 'react';
import AiMarkdown from '../../../ai/AiMarkdown';
import { Badge, Button, Card, Input, useToast } from '../../../ui';
import { saveAccountState, type AccountState } from '../../../../lib/googleAds';
import { formatDate, formatRelativeDate } from '../../../../utils/dateUtils';

interface AccountStateCardProps {
  clientId: string;
  state: AccountState | null;
  userName: string | null;
  onSaved: () => void;
}

type Draft = { status: string; goal: string; next_check: string; notes: string };

const toDraft = (s: AccountState | null): Draft => ({
  status: s?.status ?? '',
  goal: s?.goal ?? '',
  next_check: s?.next_check ?? '',
  notes: s?.notes ?? '',
});

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Denver' });

const label: React.CSSProperties = { fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' };

/**
 * Where the account is and what we are working toward, pinned above the journal. Rewritten in place;
 * the journal under it keeps the history.
 */
export default function AccountStateCard({ clientId, state, userName, onSaved }: AccountStateCardProps) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(toDraft(state));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(toDraft(state));
  }, [state, editing]);

  const save = async () => {
    setSaving(true);
    try {
      await saveAccountState(
        clientId,
        {
          status: draft.status.trim() || null,
          goal: draft.goal.trim() || null,
          next_check: draft.next_check || null,
          notes: draft.notes.trim() || null,
        },
        userName,
      );
      setEditing(false);
      onSaved();
    } catch (err) {
      console.error('Error saving account state:', err);
      toast.error('Couldn’t save the account state', err instanceof Error ? err.message : undefined);
    } finally {
      setSaving(false);
    }
  };

  const overdue = state?.next_check && state.next_check <= today();
  const empty = !state || (!state.status && !state.goal && !state.next_check && !state.notes);

  return (
    <Card
      eyebrow="Pinned"
      title="Where the account is"
      actions={
        editing ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </>
        ) : (
          <Button size="sm" variant="secondary" icon="pencil" onClick={() => setEditing(true)}>Edit</Button>
        )
      }
    >
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <Input label="Status" fieldSize="sm" value={draft.status} placeholder="Holding after the 10-07 tCPA removal; spend back to ~$20/day" onChange={(e) => setDraft({ ...draft, status: e.target.value })} />
          <Input label="Working toward" fieldSize="sm" value={draft.goal} placeholder="CPA under $40, held for 3 weeks" onChange={(e) => setDraft({ ...draft, goal: e.target.value })} />
          <div>
            <span style={label}>Next check</span>
            <input type="date" className="ck-date-input" value={draft.next_check} onChange={(e) => setDraft({ ...draft, next_check: e.target.value })} style={{ maxWidth: 200 }} />
          </div>
          <div>
            <span style={label}>Notes: guardrails, open experiments, standing rules, won’t-dos (Markdown)</span>
            <textarea className="ck-textarea" rows={7} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </div>
        </div>
      ) : empty ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
          Nothing pinned yet. Say where the account stands, what it’s working toward and when to look next.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {state?.status ? <p style={{ margin: 0, fontSize: 'var(--text-md)', color: 'var(--text-body)', fontWeight: 500, lineHeight: 1.4 }}>{state.status}</p> : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)' }}>
            {state?.goal ? (
              <div>
                <span className="t-eyebrow">Working toward</span>
                <p style={{ margin: '2px 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{state.goal}</p>
              </div>
            ) : null}
            {state?.next_check ? (
              <div>
                <span className="t-eyebrow">Next check</span>
                <p style={{ margin: '2px 0 0', fontSize: 'var(--text-sm)', display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {formatDate(`${state.next_check}T12:00:00`)}
                  {overdue ? <Badge tone="warn">due</Badge> : null}
                </p>
              </div>
            ) : null}
          </div>
          {state?.notes ? <AiMarkdown content={state.notes} /> : null}
          <span style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
            Updated {formatRelativeDate(state?.updated_at)}{state?.updated_by_name ? ` by ${state.updated_by_name}` : ''}
          </span>
        </div>
      )}
    </Card>
  );
}
